import { Action } from '@mirai/ui';
import React from 'react';

const IMAGE_MARKDOWN_REGEX = /!?\[[^\]]*]\((https?:\/\/[^\s)]+)\)/g;
const IMAGE_LIST_ITEM_REGEX = /^(?:(?:\d+\.|[-*])\s+)?(https?:\/\/[^\s)]+)\s*$/;
const IMAGE_URL_EXTENSION_REGEX = /\.(?:apng|avif|bmp|gif|jpe?g|png|svg|webp)(?:[?#][^\s)]*)?$/i;
const LIST_MARKER_ONLY_REGEX = /^(?:\d+[.)]|[-*])$/;
const LINK_TOKEN_REGEX = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s]+)/g;
const TRAILING_PUNCTUATION_REGEX = /[.,!?;:]$/;

const renderTextWithLineBreaks = (value, keyPrefix) => {
  const lines = value.split('\n');

  if (lines.length === 1) return value;

  return lines.flatMap((line, index) => {
    if (index === lines.length - 1) return line;

    return [line, <br key={`${keyPrefix}-br-${index}`} />];
  });
};

const isImageUrl = (value = '') => IMAGE_URL_EXTENSION_REGEX.test(value);

const getImageListLineUrl = (line = '') => {
  const match = line.trim().match(IMAGE_LIST_ITEM_REGEX);

  if (!match) return undefined;

  const [, imageUrl] = match;
  return isImageUrl(imageUrl) ? imageUrl : undefined;
};

const normalizeTextAfterImageExtraction = (value = '') => {
  const compactedLines = [];
  const lines = value.split('\n').map((line) => line.replace(/[ \t]+$/g, ''));

  lines.forEach((line) => {
    const isEmptyLine = line.trim() === '';

    if (!isEmptyLine) return compactedLines.push(line);
    if (!compactedLines.length || compactedLines[compactedLines.length - 1] === '') return undefined;

    return compactedLines.push('');
  });

  return compactedLines.join('\n').trim();
};

const isListMarkerOnlyLine = (value = '') => LIST_MARKER_ONLY_REGEX.test(value.trim());

export const extractImageMarkdown = (text) => {
  if (!text) return { imageUrls: [], text };

  const imageItems = [];
  const textWithoutMarkdownImages = text.replace(IMAGE_MARKDOWN_REGEX, (match, imageUrl, index) => {
    if (!isImageUrl(imageUrl)) return match;

    imageItems.push({ imageUrl, index });
    return '';
  });

  let sourceOffset = 0;
  text.split('\n').forEach((line) => {
    const imageUrl = getImageListLineUrl(line);
    if (imageUrl) imageItems.push({ imageUrl, index: sourceOffset });
    sourceOffset += line.length + 1;
  });

  if (!imageItems.length) return { imageUrls: [], text };

  const textWithoutImageList = textWithoutMarkdownImages
    .split('\n')
    .filter((line) => !getImageListLineUrl(line) && !isListMarkerOnlyLine(line))
    .join('\n');

  const imageUrls = imageItems.sort((a, b) => a.index - b.index).map(({ imageUrl }) => imageUrl);
  const nextText = normalizeTextAfterImageExtraction(textWithoutImageList);

  return { imageUrls, text: nextText };
};

export const formatText = (text) => {
  if (!text) return text;

  const hasNewlines = text.includes('\n');
  const hasLinks = LINK_TOKEN_REGEX.test(text);
  LINK_TOKEN_REGEX.lastIndex = 0;

  if (!hasLinks && !hasNewlines) return text;
  if (!hasLinks) return renderTextWithLineBreaks(text, 'text');

  const output = [];
  let lastIndex = 0;
  let match;

  while ((match = LINK_TOKEN_REGEX.exec(text)) !== null) {
    const [rawMatch, markdownLabel, markdownUrl, rawUrl] = match;
    const matchIndex = match.index;

    if (matchIndex > lastIndex) {
      const beforeText = text.slice(lastIndex, matchIndex);
      const renderedBefore = renderTextWithLineBreaks(beforeText, `text-${matchIndex}`);
      if (Array.isArray(renderedBefore)) {
        output.push(...renderedBefore);
      } else {
        output.push(renderedBefore);
      }
    }

    const linkUrl = (markdownUrl || rawUrl).replace(TRAILING_PUNCTUATION_REGEX, '');
    const linkLabel = markdownLabel || linkUrl;

    output.push(
      <Action key={`link-${matchIndex}`} href={linkUrl} paragraph rel="noopener noreferrer" target="_blank" underline>
        {linkLabel}
      </Action>,
    );

    lastIndex = matchIndex + rawMatch.length;
  }

  if (lastIndex < text.length) {
    const remainingText = text.slice(lastIndex);
    const renderedRemaining = renderTextWithLineBreaks(remainingText, `text-${lastIndex}`);
    if (Array.isArray(renderedRemaining)) {
      output.push(...renderedRemaining);
    } else {
      output.push(renderedRemaining);
    }
  }

  return output;
};
